import React, { useEffect, useState } from "react";
import Icon from "../ui/Icon";
import axiosInstance from "@/API/axiosInstance";

/**
 * NOTE:
 * This removes Redux usage and fetches directly via axiosInstance.
 * Endpoints assumed:
 *  - GET /profile            -> current user profile
 *  - GET /profile/:userId    -> other user profile
 * If your backend uses different routes, adjust `fetchOwnProfile` / `fetchUserProfile`.
 */

async function fetchOwnProfile() {
  const { data } = await axiosInstance.get("/profile");
  return data;
}

async function fetchUserProfile(userId) {
  const { data } = await axiosInstance.get(`/profile/${userId}`);
  return data;
}

export default function ProfileModal({ open, participant, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setProfile(null);
    setError(null);
    setLoading(true);

    const run = async () => {
      try {
        let response;

        // Use /profile/:id for other participants
        if (participant?.userId) {
          response = await fetchUserProfile(participant.userId);
        } else {
          // Fallback to own profile
          response = await fetchOwnProfile();
        }

        if (cancelled) return;
        setProfile(response);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load profile";
        setError(msg);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [open, participant?.userId]);

  if (!open) return null;

  // Extract user data based on actual API structure
  const user = profile?.data || null;

  const name = user?.name || participant?.name || "User";
  const email = user?.email || "";
  const phone = user?.phoneNumber || "";
  const image = user?.image || participant?.image || "";
  const bio = user?.description || "";
  const country = user?.country || "";
  const chapterName = user?.chapterName || "";
  const designation = user?.experience?.designation || "";
  const companyName = user?.experience?.companyName || "";
  const interests = user?.interests || [];
  const subscription = user?.subscription;

  const networkingRole =
    user?.networkingRole ?? participant?.networkingRole ?? "";
  const networkingIntent =
    user?.networkingIntent ?? participant?.networkingIntent ?? "";

  return (
    <div
      className="jt-modalOverlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
    >
      <div
        className="jt-sheet jt-sheetProfile"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="jt-sheetHead">
          <div className="jt-sheetTitle">Profile</div>
          <button
            className="jt-sheetClose"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="jt-sheetBody">
          {loading && (
            <div className="jt-profileLoader">Loading profile...</div>
          )}
          {error && <div className="jt-profileError">{error}</div>}

          {!loading && !error && user && (
            <div className="jt-profileContent">
              {/* Avatar and Basic Info */}
              <div className="jt-profileHeader">
                <div className="jt-profileAvatar">
                  {image ? (
                    <img src={image} alt={name} />
                  ) : (
                    <div className="jt-profileAvatarPlaceholder">
                      {(name?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="jt-profileBasic">
                  <h3 className="jt-profileName">{name}</h3>

                  {designation && (
                    <div className="jt-profileDesignation">{designation}</div>
                  )}

                  {companyName && (
                    <div className="jt-profileCompany">{companyName}</div>
                  )}

                  {subscription && (
                    <div className="jt-profileSubscription">
                      <span className="jt-subscriptionBadge">
                        {subscription.plan || "Premium"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              {(email || phone || country || chapterName) && (
                <div className="jt-profileSection">
                  <h4 className="jt-profileSectionTitle">
                    Contact Information
                  </h4>

                  <div className="jt-profileGrid">
                    {email && (
                      <div className="jt-profileField">
                        <span className="jt-profileFieldIcon">📧</span>
                        <div className="jt-profileFieldContent">
                          <span className="jt-profileFieldLabel">Email</span>
                          <span className="jt-profileFieldValue">{email}</span>
                        </div>
                      </div>
                    )}

                    {phone && (
                      <div className="jt-profileField">
                        <span className="jt-profileFieldIcon">📱</span>
                        <div className="jt-profileFieldContent">
                          <span className="jt-profileFieldLabel">Phone</span>
                          <span className="jt-profileFieldValue">{phone}</span>
                        </div>
                      </div>
                    )}

                    {country && (
                      <div className="jt-profileField">
                        <span className="jt-profileFieldIcon">🌍</span>
                        <div className="jt-profileFieldContent">
                          <span className="jt-profileFieldLabel">Country</span>
                          <span className="jt-profileFieldValue">
                            {country}
                            {user?.countryCode && ` (${user.countryCode})`}
                          </span>
                        </div>
                      </div>
                    )}

                    {chapterName && (
                      <div className="jt-profileField">
                        <span className="jt-profileFieldIcon">📍</span>
                        <div className="jt-profileFieldContent">
                          <span className="jt-profileFieldLabel">Chapter</span>
                          <span className="jt-profileFieldValue">
                            {chapterName}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bio/Description Section */}
              {bio && (
                <div className="jt-profileSection">
                  <h4 className="jt-profileSectionTitle">About</h4>
                  <p className="jt-profileBio">{bio}</p>
                </div>
              )}

              {/* Interests */}
              {interests.length > 0 && (
                <div className="jt-profileSection">
                  <h4 className="jt-profileSectionTitle">Interests</h4>
                  <div className="jt-profileInterests">
                    {interests.map((interest, idx) => (
                      <span key={idx} className="jt-interestTag">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(networkingRole || networkingIntent) && (
                <div className="jt-profileSection">
                  <h4 className="jt-profileSectionTitle">Networking</h4>

                  <div className="jt-profileGrid">
                    <div className="jt-profileField">
                      <span className="jt-profileFieldIcon">🤝</span>
                      <div className="jt-profileFieldContent">
                        <span className="jt-profileFieldLabel">Role</span>
                        <span className="jt-profileFieldValue">
                          {networkingRole || "Not set"}
                        </span>
                      </div>
                    </div>

                    <div className="jt-profileField">
                      <span className="jt-profileFieldIcon">🎯</span>
                      <div className="jt-profileFieldContent">
                        <span className="jt-profileFieldLabel">Intent</span>
                        <span className="jt-profileFieldValue">
                          {networkingIntent || "No intent provided"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
